import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PublicLayout, OperatorLayout, ParentLayout } from './components/layouts';
import './App.css';

// Public Pages
import { PublicLanding } from './pages/PublicLanding';
import { WaitlistLanding } from './pages/WaitlistLanding';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { TermsOfService } from './pages/TermsOfService';
import { ParentLogin } from './pages/ParentLogin';
import { OperatorLogin } from './pages/OperatorLogin';
import { InviteAccept } from './pages/InviteAccept';

// Auth Pages (self-serve)
import { Signup } from './pages/auth/Signup';
import { OnboardingWizard } from './pages/onboarding/OnboardingWizard';
import { Subscribe } from './pages/Subscribe';

// Parent Pages
import { ParentHome } from './pages/parent/ParentHome';
import { ParentSettings } from './pages/parent/ParentSettings';
import { ParentOnboarding } from './pages/parent/ParentOnboarding';
import { ParentToday } from './pages/parent/ParentToday';
import { ParentWeek } from './pages/parent/ParentWeek';
import { ParentUpdates } from './pages/parent/ParentUpdates';
import { ParentCare } from './pages/parent/ParentCare';

// Operator Pages
import { OperatorOverview } from './pages/operator/OperatorOverview';
import { OperatorFamilies } from './pages/operator/OperatorFamilies';
import { OperatorFamilyDetail } from './pages/operator/OperatorFamilyDetail';
import { OperatorQueue } from './pages/operator/OperatorQueue';
import { OperatorNewFamily } from './pages/operator/OperatorNewFamily';
import { OperatorDebug } from './pages/operator/OperatorDebug';
import { OperatorReliability } from './pages/operator/OperatorReliability';
import OperatorExecutionLogs from './pages/operator/OperatorExecutionLogs';
import TestDataCleanup from './pages/operator/TestDataCleanup';

/**
 * Protected Route for Parent-only pages
 * Checks onboarding status and redirects to /onboarding if not configured
 */
const ParentRoute = ({ children, requireConfigured = true }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isConfigured, setIsConfigured] = useState(null);
  const [subscriptionOk, setSubscriptionOk] = useState(null);
  const [checkingConfig, setCheckingConfig] = useState(true);

  const familyId = user?.familyId || searchParams.get('family_id') || localStorage.getItem('family_id');

  useEffect(() => {
    const checkOnboardingAndSubscription = async () => {
      if (!user || user.role !== 'parent' || !familyId) {
        setCheckingConfig(false);
        return;
      }

      // Skip check for onboarding and settings pages
      if (location.pathname.includes('/onboarding') || location.pathname.includes('/settings')) {
        setIsConfigured(true);
        setSubscriptionOk(true);
        setCheckingConfig(false);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/families/${familyId}`,
          {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
          }
        );
        const data = await response.json();
        setIsConfigured(data.is_configured === true);

        // Check subscription status
        const subStatus = data.subscription_status;
        if (subStatus === 'active') {
          setSubscriptionOk(true);
        } else if (subStatus === 'trial') {
          const trialEnds = data.trial_ends_at;
          setSubscriptionOk(trialEnds ? new Date(trialEnds) > new Date() : false);
        } else if (!subStatus) {
          // No subscription_status column yet — fail open for existing families
          setSubscriptionOk(true);
        } else {
          setSubscriptionOk(false);
        }
      } catch (err) {
        console.error('Failed to check config:', err);
        setIsConfigured(true); // Fail open
        setSubscriptionOk(true); // Fail open
      }
      setCheckingConfig(false);
    };

    if (!loading) {
      checkOnboardingAndSubscription();
    }
  }, [user, loading, familyId, location.pathname]);

  if (loading || checkingConfig) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!user || user.role !== 'parent') {
    const fullPath = location.pathname + location.search;
    if (fullPath.includes('gmail=') || fullPath.includes('family_id=')) {
      sessionStorage.setItem('post_login_redirect', fullPath);
    }
    return <Navigate to="/login" state={{ from: fullPath }} replace />;
  }

  // Redirect to onboarding if not configured
  if (requireConfigured && isConfigured === false) {
    return <Navigate to={`/onboarding?family_id=${familyId}`} replace />;
  }

  // Redirect to subscribe if no valid subscription
  if (subscriptionOk === false) {
    return <Navigate to="/subscribe" replace />;
  }

  return children;
};

/**
 * Protected Route for Operator-only pages
 */
const OperatorRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  if (!user || user.role !== 'operator') {
    return <Navigate to="/operator/login" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Landing Page */}
      <Route path="/" element={<WaitlistLanding />} />
      <Route path="/full" element={<PublicLanding />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />

      {/* Public Auth Routes (no sidebar) */}
      <Route path="/login" element={
        <PublicLayout>
          <ParentLogin />
        </PublicLayout>
      } />
      <Route path="/signup" element={
        <PublicLayout>
          <Signup />
        </PublicLayout>
      } />
      <Route path="/operator/login" element={
        <PublicLayout>
          <OperatorLogin />
        </PublicLayout>
      } />
      <Route path="/invite/:token" element={
        <PublicLayout>
          <InviteAccept />
        </PublicLayout>
      } />
      
      {/* Self-serve Onboarding Wizard (no sidebar) */}
      <Route path="/onboarding" element={
        <PublicLayout>
          <OnboardingWizard />
        </PublicLayout>
      } />
      
      {/* Parent Onboarding (legacy - no sidebar - full screen wizard) */}
      <Route path="/parent/onboarding/:familyId" element={
        <PublicLayout>
          <ParentOnboarding />
        </PublicLayout>
      } />
      
      {/* Subscription paywall (requires auth, no sidebar) */}
      <Route path="/subscribe" element={<Subscribe />} />

      {/* Parent Routes (with ParentSidebar) */}
      <Route path="/parent" element={
        <ParentRoute>
          <ParentLayout>
            <ParentToday />
          </ParentLayout>
        </ParentRoute>
      } />
      <Route path="/parent/home" element={
        <ParentRoute>
          <ParentLayout>
            <ParentToday />
          </ParentLayout>
        </ParentRoute>
      } />
      <Route path="/parent/today" element={
        <ParentRoute>
          <ParentLayout>
            <ParentToday />
          </ParentLayout>
        </ParentRoute>
      } />
      <Route path="/parent/week" element={
        <ParentRoute>
          <ParentLayout>
            <ParentWeek />
          </ParentLayout>
        </ParentRoute>
      } />
      <Route path="/parent/updates" element={
        <ParentRoute>
          <ParentLayout>
            <ParentUpdates />
          </ParentLayout>
        </ParentRoute>
      } />
      <Route path="/parent/care" element={
        <ParentRoute>
          <ParentLayout>
            <ParentCare />
          </ParentLayout>
        </ParentRoute>
      } />
      {/* Backward compat: old /inbox bookmarks redirect to /updates */}
      <Route path="/parent/inbox" element={
        <ParentRoute>
          <ParentLayout>
            <ParentUpdates />
          </ParentLayout>
        </ParentRoute>
      } />
      {/* Nanny removed from nav — redirect to Today */}
      <Route path="/parent/nanny" element={<Navigate to="/parent" replace />} />
      <Route path="/parent/settings" element={
        <ParentRoute>
          <ParentLayout>
            <ParentSettings />
          </ParentLayout>
        </ParentRoute>
      } />
      
      {/* Operator Routes (with OperatorSidebar) */}
      <Route path="/operator" element={
        <OperatorRoute>
          <OperatorLayout>
            <OperatorOverview />
          </OperatorLayout>
        </OperatorRoute>
      } />
      <Route path="/operator/families" element={
        <OperatorRoute>
          <OperatorLayout>
            <OperatorFamilies />
          </OperatorLayout>
        </OperatorRoute>
      } />
      <Route path="/operator/family/:familyId" element={
        <OperatorRoute>
          <OperatorLayout>
            <OperatorFamilyDetail />
          </OperatorLayout>
        </OperatorRoute>
      } />
      <Route path="/operator/queue" element={
        <OperatorRoute>
          <OperatorLayout>
            <OperatorQueue />
          </OperatorLayout>
        </OperatorRoute>
      } />
      <Route path="/operator/new-family" element={
        <OperatorRoute>
          <OperatorLayout>
            <OperatorNewFamily />
          </OperatorLayout>
        </OperatorRoute>
      } />
      <Route path="/operator/debug" element={
        <OperatorRoute>
          <OperatorLayout>
            <OperatorDebug />
          </OperatorLayout>
        </OperatorRoute>
      } />
      <Route path="/operator/reliability" element={
        <OperatorRoute>
          <OperatorLayout>
            <OperatorReliability />
          </OperatorLayout>
        </OperatorRoute>
      } />
      <Route path="/operator/execution-logs" element={
        <OperatorRoute>
          <OperatorLayout>
            <OperatorExecutionLogs />
          </OperatorLayout>
        </OperatorRoute>
      } />
      <Route path="/operator/test-data-cleanup" element={
        <OperatorRoute>
          <OperatorLayout>
            <TestDataCleanup />
          </OperatorLayout>
        </OperatorRoute>
      } />
      
      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
